// Point restriction of a new API token: the mode, a point picker (search,
// site filter, paging) for "specific points" and "all enabled except", and a
// tag input with suggestions for "by tag". The API checks it again.
import { useState } from 'react';
import type { Point, Site } from './api/types.js';
import { useApi } from './auth.js';
import { ErrorBox, Spinner } from './components.js';
import { useDebounced, useResource } from './hooks.js';
import { formatCount } from './lib/format.js';
import { pageInfo } from './lib/paging.js';
import {
  normalizeTagFilter,
  POINT_ACCESS_LIMITS,
  POINT_MODES,
  type PointMode,
} from './lib/tokens.js';

export const PICKER_PAGE = 20;

/** What the picker shows for a picked point (name, else key). */
export const pointLabel = (p: Pick<Point, 'name' | 'point_key'>) => p.name || p.point_key;

function PointPicker({
  sites,
  anySite,
  picked,
  onChange,
}: {
  /** Sites to search in (the token's sites, else all the caller sees). */
  sites: readonly Site[];
  /** Whether "All sites" may be searched at once (the token is not limited to sites). */
  anySite: boolean;
  picked: ReadonlyMap<string, Point>;
  onChange: (next: ReadonlyMap<string, Point>) => void;
}) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [siteId, setSiteId] = useState('');
  const [offset, setOffset] = useState(0);
  // A site no longer offered (token sites changed) falls back to all sites or the first one.
  const site = sites.some((s) => s.id === siteId) ? siteId : anySite ? '' : (sites[0]?.id ?? '');
  const key = JSON.stringify(['token-points', q, site, offset]);
  const result = useResource(key, (signal) =>
    api.points(
      { q: q || undefined, site_id: site || undefined, limit: PICKER_PAGE, offset },
      signal,
    ),
  );
  const items = result.data?.items ?? [];
  const info = result.data ? pageInfo(result.data.total, offset, PICKER_PAGE) : null;

  function set(points: readonly Point[], on: boolean) {
    const next = new Map(picked);
    for (const p of points) {
      if (on) next.set(p.id, p);
      else next.delete(p.id);
    }
    onChange(next);
  }

  const allOnPage = items.length > 0 && items.every((p) => picked.has(p.id));

  return (
    <div className="token-points">
      <div className="toolbar">
        <input
          type="search"
          className="search"
          placeholder="Search name, key, description…"
          aria-label="Search points"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOffset(0);
          }}
        />
        {sites.length > 1 && (
          <select
            aria-label="Site"
            value={site}
            onChange={(e) => {
              setSiteId(e.target.value);
              setOffset(0);
            }}
          >
            {anySite && <option value="">All sites</option>}
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {result.error && <ErrorBox error={result.error} onRetry={result.reload} />}
      {!result.data ? (
        !result.error && <Spinner />
      ) : items.length === 0 ? (
        <p className="muted small">No points match.</p>
      ) : (
        <div className="table-wrap token-points-table">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Pick all points on this page"
                    checked={allOnPage}
                    onChange={(e) => {
                      set(items, e.target.checked);
                    }}
                  />
                </th>
                <th>Name</th>
                <th>Description</th>
                <th>Key</th>
                <th>Device</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Pick ${pointLabel(p)}`}
                      checked={picked.has(p.id)}
                      onChange={(e) => {
                        set([p], e.target.checked);
                      }}
                    />
                  </td>
                  <td className="strong">{p.name}</td>
                  <td className="small">{p.description}</td>
                  <td className="mono small">{p.point_key}</td>
                  <td className="small">
                    <span className="mono">{p.device.sparkplug_device_id}</span>
                    <div className="muted">
                      {p.site.name} · {p.edge.edge_node_id}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {info && info.total > PICKER_PAGE && (
        <div className="pager">
          <span className="muted small">
            {formatCount(info.first)}–{formatCount(info.last)} of {formatCount(info.total)}
          </span>
          <button
            type="button"
            className="btn btn-small"
            disabled={!info.hasPrev}
            onClick={() => {
              setOffset(Math.max(0, offset - PICKER_PAGE));
            }}
          >
            Previous
          </button>
          <button
            type="button"
            className="btn btn-small"
            disabled={!info.hasNext}
            onClick={() => {
              setOffset(offset + PICKER_PAGE);
            }}
          >
            Next
          </button>
        </div>
      )}
      <div className="token-picked">
        <span className="strong small">
          {picked.size === 0 ? 'None picked' : `${formatCount(picked.size)} picked`}
          {picked.size > POINT_ACCESS_LIMITS.ids &&
            ` (at most ${formatCount(POINT_ACCESS_LIMITS.ids)})`}
        </span>
        {picked.size > 0 && (
          <button
            type="button"
            className="btn btn-small"
            onClick={() => {
              onChange(new Map());
            }}
          >
            Clear
          </button>
        )}
        <span className="tag-chips">
          {[...picked.values()].map((p) => (
            <button
              key={p.id}
              type="button"
              className="tag"
              title={`${p.point_key} — remove`}
              aria-label={`Remove ${pointLabel(p)}`}
              onClick={() => {
                set([p], false);
              }}
            >
              {pointLabel(p)} ×
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}

function TagPicker({
  tags,
  onChange,
}: {
  tags: readonly string[];
  onChange: (next: string[]) => void;
}) {
  const api = useApi();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const known = useResource('token-tags', (signal) => api.tags({}, signal));
  const suggestions = [
    ...new Set([...(known.data ?? []).map((t) => t.key), ...(known.data ?? []).map((t) => t.tag)]),
  ].sort();

  function add() {
    const t = normalizeTagFilter(text);
    if (!t) {
      setError('Use key=value or a key (a lowercase letter, then letters, digits, _ or -).');
      return;
    }
    setError(null);
    setText('');
    if (!tags.includes(t)) onChange([...tags, t]);
  }

  return (
    <div className="token-points">
      <div className="toolbar">
        <input
          type="text"
          className="search"
          list="token-tag-suggestions"
          placeholder="building=E or room"
          aria-label="Tag"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <datalist id="token-tag-suggestions">
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <button type="button" className="btn btn-small" disabled={!text.trim()} onClick={add}>
          Add tag
        </button>
      </div>
      {error && <p className="form-error">{error}</p>}
      <div className="token-picked">
        <span className="strong small">
          {tags.length === 0 ? 'No tags yet' : `Any of ${String(tags.length)}:`}
        </span>
        <span className="tag-chips">
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              className="tag"
              aria-label={`Remove ${t}`}
              onClick={() => {
                onChange(tags.filter((x) => x !== t));
              }}
            >
              {t} ×
            </button>
          ))}
        </span>
      </div>
    </div>
  );
}

/** The "Points" fieldset of the token form. */
export function TokenPointsField({
  mode,
  onMode,
  sites,
  anySite,
  picked,
  onPicked,
  tags,
  onTags,
  problem,
}: {
  mode: PointMode;
  onMode: (m: PointMode) => void;
  sites: readonly Site[] | undefined;
  anySite: boolean;
  picked: ReadonlyMap<string, Point>;
  onPicked: (next: ReadonlyMap<string, Point>) => void;
  tags: readonly string[];
  onTags: (next: string[]) => void;
  problem: string | null;
}) {
  const info = POINT_MODES.find((m) => m.mode === mode);
  return (
    <fieldset className="token-scopes">
      <legend className="strong">Points</legend>
      <div className="segmented" role="group" aria-label="Points">
        {POINT_MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            className={m.mode === mode ? 'active' : undefined}
            aria-pressed={m.mode === mode}
            onClick={() => {
              onMode(m.mode);
            }}
          >
            {m.label}
          </button>
        ))}
      </div>
      {info && <p className="muted small">{info.help}</p>}
      {(mode === 'list' || mode === 'enabled') &&
        (sites ? (
          <PointPicker sites={sites} anySite={anySite} picked={picked} onChange={onPicked} />
        ) : (
          <Spinner />
        ))}
      {mode === 'tags' && <TagPicker tags={tags} onChange={onTags} />}
      {problem && <p className="form-error">{problem}</p>}
    </fieldset>
  );
}
